<template>
  <BaseCard>
    <div v-if="showSuccess" class="flex flex-col gap-y-4 p-4 w-full text-center" role="status" aria-live="polite">

      <p class="body-sm">
        Your password has successfully been reset.<br /> If you are not redirected automatically, please click the button below to go to your dashboard.
      </p>

      <BaseButton
        class="btn--primary self-center"
        text="dashboard"
        type="button"
        @click="goToDashboard"
      />
    </div>

    <form
      v-else
      class="flex max-w-xl flex-col gap-y-6"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <BaseFormErrorSummary
        :errors="errors"
        :submit-count="submitCount"
      />

      <BaseInlineNotification
        v-if="submissionError"
        heading-text="An Error Occurred"
        hide-title-element
        type="error"
      >
        <p
          class="body-sm"
          v-text="submissionError"
        />
      </BaseInlineNotification>

      <div class="flex flex-col gap-4">
        <BasePasswordInput
          id="password"
          v-model="values.password"
          :error-message="errors.password"
          autocomplete="new-password"
          label="Password"
          name="password"
          required
        />

        <BasePasswordValidation
          :error="errors.password"
          :password="values.password"
          show-validation
        />
      </div>

      <BasePasswordInput
        id="confirmPassword"
        v-model="values.confirmPassword"
        :error-message="errors.confirmPassword"
        autocomplete="new-password"
        label="Confirm Password"
        name="confirmPassword"
        required
        validation-rules="required|confirmPassword:@password"
      />

      <BaseButton
        :disabled="isSubmitting"
        class="btn--primary md:self-start"
        text="Reset Password"
        type="submit"
      />

      <BaseRecaptchaNotice text-alignment="left" />
    </form>
  </BaseCard>
</template>

<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useForm } from 'vee-validate'

import BaseCard from '@js/components/global/BaseCard.vue'
import BaseButton from '@js/components/global/BaseButton.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BasePasswordInput from '@js/components/global/forms/inputs/BasePasswordInput.vue'
import BasePasswordValidation from '@js/components/global/forms/BasePasswordValidation.vue'
import BaseRecaptchaNotice from '../global/forms/BaseRecaptchaNotice.vue'
import useRecaptcha from '@js/composables/useRecaptcha'

import { log } from '@js/utils/logger'
import useAuth from '@js/composables/useAuth'
import { accountLinks } from '@js/utils/links'
import { resetPassword } from '@js/api/auth/resetPassword'

defineOptions({
  name: 'AppResetPasswordForm',
})

const {
  values,
  errors,
  resetForm,
  submitCount,
  isSubmitting,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    password: '',
    confirmPassword: '',
  },
})

const { logInUser } = useAuth()
const { loadRecaptcha, getRecaptchaToken } = useRecaptcha()

const submissionError = ref('')
const showSuccess = ref(false)

let redirectTimer: number | null = null

const goToDashboard = (): void => {
  clearTimers()
  window.location.assign(accountLinks.DASHBOARD)
}

const startRedirect = (): void => {
  redirectTimer = window.setTimeout(goToDashboard, 5_000)
}

const clearTimers = (): void => {
  if (redirectTimer) {
    clearTimeout(redirectTimer)
    redirectTimer = null
  }
}

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  try {
    const token = await getRecaptchaToken('reset_password')
    if (!token) {
      submissionError.value = 'Security verification failed. Please try again.'
      return
    }

    submissionError.value = ''

    const searchParams = new URLSearchParams(window.location.search)
    const resetToken = searchParams.get('token') ?? ''
    const userEmail = (searchParams.get('email') ?? '').replace(/\s+/g, '+')

    await resetPassword({
      email: userEmail,
      newPassword: values.password,
      resetPasswordToken: resetToken,
      recaptchaToken: token,
    })

    await logInUser({
      email: userEmail,
      password: values.password,
    })

    showSuccess.value = true
    
    startRedirect()
    resetForm()
  } catch (error) {
    submissionError.value = 'An error occurred. Please try again.'

    log({
      message: 'Reset Password Error',
      file: 'src/wcei/js/components/auth/AppResetPasswordForm.vue',
    })
  }
})

onMounted(() => {
  loadRecaptcha()
})

onUnmounted(clearTimers)
</script>
